import React from "react";
import { useForm, useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import { UncontrolledSwitch } from "src/ui/shared/form/SwitchField";
import { Tooltip } from "src/ui/shared/Tooltip";
import { makeStyles } from "tss-react/mui";

import { SectionDescription } from "@dashboard/shared/styles";
import { Box } from "@mui/material";

import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
    useCollapsiblePersistence,
} from "../../../shared/components/CollapsibleSection";
import { FormValues } from "../../ProvisioningPage";
import { Connection, ConnectionName } from "../../types";
import { formatSummaryList } from "../../utils/sectionSummary";

import { getCheckedFields, getConnections, getLastEnabledConnection, getRequiredSections } from "./InternetConfig.utils";
import { WifiConnection } from "./WifiConnection";

const useStyles = makeStyles()((theme) => ({
    list: {
        paddingLeft: theme.spacing(5),
    },
    item: {
        marginTop: theme.spacing(1),
    },
}));

export const InternetConfig = () => {
    const { value, onChange } = useCollapsiblePersistence("provisioningPage.collapsedSections", "InternetConfig");

    const { classes } = useStyles();
    const parentForm = useForm();
    const { t, i18n } = useTranslation(["pages"]);
    const { values: parentFormValues } = useFormState<Partial<FormValues>>();
    const internetConfig = parentFormValues?.settings?.internetConfig || [];

    const [hasRequiredSectionEnabled, enabledRequiredSections] = getRequiredSections(parentFormValues || {});
    const lastEnabledConnection = getLastEnabledConnection(internetConfig);
    const checkedFields = getCheckedFields(internetConfig);

    const wifiNetworks = internetConfig.find((item) => item.type === "wifi")?.networks?.length || 0;

    const summaryLabel: Record<ConnectionName, string> = {
        ethernet: t("pages:provisioningPage.summaryLabel.ethernet"),
        modem: t("pages:provisioningPage.summaryLabel.modem"),
        wifi: t("pages:provisioningPage.section.internetConfig.summaryWifi", { count: wifiNetworks }),
    };
    const connectionLabels = getConnections(internetConfig).map((name) => summaryLabel[name]);
    const summary = connectionLabels.length ? formatSummaryList(connectionLabels, i18n.language) : undefined;

    const disableWifiField = hasRequiredSectionEnabled && lastEnabledConnection === "wifi";
    const disableEthernetField = hasRequiredSectionEnabled && lastEnabledConnection === "ethernet";
    const disableModemField = hasRequiredSectionEnabled && lastEnabledConnection === "modem";

    const onSwitchChange = (name: ConnectionName, checked: boolean) => {
        const connectionExist = internetConfig.find((item) => item.type === name);

        let newInternetConfig: Connection[] = [];

        if (connectionExist && !checked) {
            newInternetConfig = internetConfig.filter((item) => item.type !== name);
        }
        if (!connectionExist && checked) {
            const newItem = name === "wifi" ? { type: name, networks: [] } : { type: name };
            newInternetConfig = [...internetConfig, newItem];
        }

        parentForm.change("settings.internetConfig", newInternetConfig);
    };
    const getTooltipText = (name: ConnectionName) => {
        if (lastEnabledConnection === name && enabledRequiredSections.length) {
            return (
                <React.Fragment>
                    {t("pages:provisioningPage.tooltip.internetConfig")}
                    <ul className={classes.list}>
                        {enabledRequiredSections.map((item) => (
                            <li key={item} className={classes.item}>
                                <strong>{item}</strong>
                            </li>
                        ))}
                    </ul>
                </React.Fragment>
            );
        }
        return undefined;
    };

    return (
        <CollapsibleProvider initialValue={value} onChange={onChange}>
            <CollapsibleSectionSummary>
                <CollapsibleSectionTitle summary={summary}>
                    {t("pages:provisioningPage.section.internetConfig.title")}
                </CollapsibleSectionTitle>
            </CollapsibleSectionSummary>
            <CollapsibleSectionDetails>
                <SectionDescription>{t("pages:provisioningPage.section.internetConfig.description")}</SectionDescription>

                <Box>
                    <Tooltip title={getTooltipText("ethernet")} placement="right">
                        <UncontrolledSwitch
                            name="ethernet-switch"
                            label={t("pages:provisioningPage.input.ethernetSwitch.caption")}
                            labelPlacement="end"
                            value={checkedFields.includes("ethernet")}
                            onChange={(_e, checked) => onSwitchChange("ethernet", checked)}
                            disabled={disableEthernetField}
                        />
                    </Tooltip>
                </Box>
                <Box>
                    <Tooltip title={getTooltipText("modem")} placement="right">
                        <UncontrolledSwitch
                            name="modem-switch"
                            label={t("pages:provisioningPage.input.modemSwitch.caption")}
                            labelPlacement="end"
                            value={checkedFields.includes("modem")}
                            onChange={(_e, checked) => onSwitchChange("modem", checked)}
                            disabled={disableModemField}
                        />
                    </Tooltip>
                </Box>
                <Box>
                    <Tooltip title={getTooltipText("wifi")} placement="right">
                        <UncontrolledSwitch
                            name="wifi-switch"
                            label={t("pages:provisioningPage.input.wifiSwitch.caption")}
                            labelPlacement="end"
                            value={checkedFields.includes("wifi")}
                            onChange={(_e, checked) => onSwitchChange("wifi", checked)}
                            disabled={disableWifiField}
                        />
                    </Tooltip>
                    {checkedFields.includes("wifi") ? <WifiConnection /> : null}
                </Box>
            </CollapsibleSectionDetails>
        </CollapsibleProvider>
    );
};
